import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CreditCard, Building2, QrCode, Loader2, CheckCircle, ArrowLeft, Copy, Upload, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// DuitNow QR Code
const DUITNOW_QR = "https://customer-assets.emergentagent.com/job_protect-workers/artifacts/qjoiqcjn_image.png";


const PACKAGES = {
  "1": { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null },
  "3": { months: 3, pricePerMonth: 19, total: 57, savings: 3, discount: "5%" },
  "6": { months: 6, pricePerMonth: 18, total: 108, savings: 12, discount: "10%" },
  "12": { months: 12, pricePerMonth: 17, total: 204, savings: 36, discount: "15%", bestValue: true },
  "subscription": { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null, recurring: true }
};

export default function PaymentPage({ user }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || "1");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [proofImage, setProofImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Fetch bank details
    const fetchBankDetails = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/payment/packages`);
        setBankDetails(response.data.bank_details);
      } catch (error) {
        console.error("Failed to fetch bank details:", error);
      }
    };
    fetchBankDetails();
    
    // Check if coming from registration with a transaction already created
    const methodFromUrl = searchParams.get('method');
    const transactionIdFromUrl = searchParams.get('transaction_id');
    
    console.log("Payment page loaded:", { methodFromUrl, transactionIdFromUrl });
    
    if (transactionIdFromUrl && methodFromUrl) {
      // Transaction already created during registration, just show the payment details
      console.log("Setting payment method from URL:", methodFromUrl);
      setPaymentMethod(methodFromUrl);
      setTransaction({ transaction_id: transactionIdFromUrl });
      if (methodFromUrl === 'subscription') {
        setSelectedPlan('subscription');
      }
    } else if (methodFromUrl) {
      // Auto-trigger payment method selection
      if (methodFromUrl === 'subscription') {
        setSelectedPlan('subscription');
      }
      setTimeout(() => {
        handlePaymentMethodSelect(methodFromUrl);
      }, 500);
    }
  }, [searchParams]);

  const handlePaymentMethodSelect = async (method) => {
    setPaymentMethod(method);
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${BACKEND_URL}/api/payment/create-session`,
        {
          package_id: selectedPlan,
          payment_method: method,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTransaction(response.data);

      if ((method === "fpx" || method === "subscription") && response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment session");
      setPaymentMethod(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleProofUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProofImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitManualPayment = async () => {
    if (!referenceNumber.trim()) {
      toast.error("Please enter the reference number");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${BACKEND_URL}/api/payment/submit-manual`,
        {
          transaction_id: transaction.transaction_id,
          reference_number: referenceNumber,
          proof_image: proofImage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Payment submitted for verification!");
      navigate("/dashboard?payment=pending");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit payment");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPackage = PACKAGES[selectedPlan];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4 relative overflow-hidden">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate("/dashboard")} 
            className="px-3 py-2"
            data-testid="payment-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_protect-workers/artifacts/awekt278_image.png" 
              alt="MyShield Logo" 
              className="h-14 w-auto"
            />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Complete Your Payment</h1>
              <p className="text-slate-600">Secure payment for MyShield membership</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Order Summary */}
          <Card className="lg:col-span-1 h-fit">
            <CardHeader>
              <CardTitle className="text-lg">Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Plan Selector */}
              <div className="space-y-2">
                <Label>Select Plan</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(PACKAGES).map(([key, pkg]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedPlan(key)}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        selectedPlan === key
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`plan-select-${key}m`}
                    >
                      <div className="font-bold text-lg">{pkg.months}</div>
                      <div className="text-xs text-slate-500">{pkg.months === 1 ? 'month' : 'months'}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Price per month</span>
                  <span className="font-medium">RM {selectedPackage.pricePerMonth}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Duration</span>
                  <span className="font-medium">{selectedPackage.months} {selectedPackage.months === 1 ? 'month' : 'months'}</span>
                </div>
                {selectedPackage.savings > 0 && (
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>You save</span>
                    <span className="font-medium">RM {selectedPackage.savings}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                  <span>Total</span>
                  <span>RM {selectedPackage.total}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Methods */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">
                {!paymentMethod ? "Choose Payment Method" : "Complete Payment"}
              </CardTitle>
              {!paymentMethod && (
                <CardDescription>Select how you would like to pay</CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {!paymentMethod ? (
                <div className="space-y-4">
                  {/* Card Payment Option */}
                  <button
                    onClick={() => handlePaymentMethodSelect("fpx")}
                    disabled={loading}
                    className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all flex items-center gap-4 text-left"
                    data-testid="payment-method-fpx"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">Card Payment</div>
                      <div className="text-sm text-slate-500">One-time payment with debit/credit card</div>
                    </div>
                  </button>

                  {/* Auto-Debit Subscription Option */}
                  <button
                    onClick={() => {
                      setSelectedPlan("subscription");
                      handlePaymentMethodSelect("subscription");
                    }}
                    disabled={loading}
                    className="w-full p-4 border-2 border-emerald-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all flex items-center gap-4 text-left"
                    data-testid="payment-method-subscription"
                  >
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <RefreshCw className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">Auto-Debit Monthly</div>
                      <div className="text-sm text-slate-500">RM20/month - Cancel anytime</div>
                    </div>
                    <div className="text-emerald-600 font-medium text-sm">Recommended</div>
                  </button>

                  {/* QR Code Option */}
                  <button
                    onClick={() => handlePaymentMethodSelect("qr_code")}
                    disabled={loading}
                    className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-slate-400 hover:bg-slate-50 transition-all flex items-center gap-4 text-left"
                    data-testid="payment-method-qr"
                  >
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                      <QrCode className="w-6 h-6 text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">DuitNow QR</div>
                      <div className="text-sm text-slate-500">Scan QR code to pay</div>
                    </div>
                  </button>

                  {/* Bank Transfer Option */}
                  <button
                    onClick={() => handlePaymentMethodSelect("bank_transfer")}
                    disabled={loading}
                    className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-slate-400 hover:bg-slate-50 transition-all flex items-center gap-4 text-left"
                    data-testid="payment-method-bank"
                  >
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">Bank Transfer</div>
                      <div className="text-sm text-slate-500">Transfer directly to our account</div>
                    </div>
                  </button>

                  {loading && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                      <span className="ml-2 text-slate-600">Processing...</span>
                    </div>
                  )}
                </div>
              ) : paymentMethod === "qr_code" ? (
                /* QR Code Payment */
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="inline-block p-4 bg-white rounded-xl shadow-lg border">
                      <img src={DUITNOW_QR} alt="DuitNow QR Code" className="w-48 h-48 mx-auto" />
                    </div>
                    <p className="mt-4 text-slate-600">Scan with your banking app</p>
                    <p className="font-bold text-xl text-slate-900">RM {selectedPackage.total}</p>
                  </div>

                  <div className="border-t pt-4 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="ref-number">Reference Number / Transaction ID</Label>
                      <Input
                        id="ref-number"
                        placeholder="Enter your payment reference number"
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        data-testid="payment-reference-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Upload Payment Proof (Optional)</Label>
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleProofUpload}
                          className="hidden"
                          id="proof-upload"
                        />
                        <label htmlFor="proof-upload" className="cursor-pointer">
                          {proofImage ? (
                            <img src={proofImage} alt="Payment proof" className="max-w-full h-32 object-contain mx-auto" />
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mx-auto text-slate-400" />
                              <p className="mt-2 text-sm text-slate-500">Click to upload screenshot</p>
                            </>
                          )}
                        </label>
                      </div>
                    </div>

                    <Button
                      onClick={handleSubmitManualPayment}
                      disabled={submitting || !referenceNumber.trim()}
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      data-testid="submit-payment-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        "Submit Payment"
                      )}
                    </Button>
                  </div>
                </div>
              ) : paymentMethod === "bank_transfer" ? (
                /* Bank Transfer */
                <div className="space-y-6">
                  <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                    <h3 className="font-semibold text-slate-900">Bank Account Details</h3>
                    
                    {bankDetails && (
                      <>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm text-slate-500">Bank Name</p>
                            <p className="font-medium">{bankDetails.bank_name}</p>
                          </div>
                        </div>

                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm text-slate-500">Account Name</p>
                            <p className="font-medium">{bankDetails.account_name}</p>
                          </div>
                        </div>

                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm text-slate-500">Account Number</p>
                            <p className="font-medium font-mono">{bankDetails.account_number}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyToClipboard(bankDetails.account_number)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    )}

                    <div className="flex justify-between items-center border-t pt-4">
                      <div>
                        <p className="text-sm text-slate-500">Amount to Transfer</p>
                        <p className="font-bold text-xl text-slate-900">RM {selectedPackage.total}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyToClipboard(selectedPackage.total.toString())}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="bank-ref">Reference Number / Transaction ID</Label>
                      <Input
                        id="bank-ref"
                        placeholder="Enter your bank transfer reference"
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        data-testid="bank-reference-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Upload Transfer Receipt (Optional)</Label>
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleProofUpload}
                          className="hidden"
                          id="bank-proof-upload"
                        />
                        <label htmlFor="bank-proof-upload" className="cursor-pointer">
                          {proofImage ? (
                            <img src={proofImage} alt="Transfer receipt" className="max-w-full h-32 object-contain mx-auto" />
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mx-auto text-slate-400" />
                              <p className="mt-2 text-sm text-slate-500">Click to upload receipt screenshot</p>
                            </>
                          )}
                        </label>
                      </div>
                    </div>

                    <Button
                      onClick={handleSubmitManualPayment}
                      disabled={submitting || !referenceNumber.trim()}
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      data-testid="submit-bank-payment-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        "Submit Payment"
                      )}
                    </Button>
                  </div>
                </div>
              ) : null}

              {paymentMethod && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setPaymentMethod(null);
                    setTransaction(null);
                    setReferenceNumber("");
                    setProofImage(null);
                  }}
                  className="w-full mt-4"
                >
                  Choose Different Payment Method
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Security Badge */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <div className="flex items-center justify-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            <span>Secure payment • SSL encrypted</span>
          </div>
        </div>

        {/* Cancel Link */}
        <div className="mt-4 text-center">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-slate-500 hover:text-slate-700 text-sm underline"
            data-testid="cancel-payment-link"
          >
            Cancel and go back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
